/* *************************************************************************************************
 ******************************************** ENUMS ************************************************
 ************************************************************************************************ */

export enum TemperatureType {
  c = '°C',
  f = '°F',
}

export enum PressureType {
  bar = 'bar',
  MPa = 'MPa',
  PSI = 'PSI',
}

export enum LengthType {
  inch = '"',
  mm = 'mm',
}

export enum SortOrder {
  ascend = 'ascend',
  descend = 'descend',
}

export enum MeasurementType {
  length = 'length',
  pressure = 'pressure',
  temperature = 'temperature',
  time = 'time',
}

export enum EdgeDeviceOperationalStatusStatus {
  on = 'on',
  off = 'off',
}

export enum EquipmentCategory {
  crusher = 'crusher',
  roll = 'roll',
  screen = 'screen',
  washer = 'washer',
}

export enum EquipmentUnitInstallStatus {
  pre = 'pre',
  installed = 'installed',
  removed = 'removed',
}

export enum UserStatus {
  active = 'active',
  inactive = 'inactive',
  invited = 'invited',
  removed = 'removed',
}

export enum UserType {
  admin = 'admin',
  customer = 'customer',
  service = 'service',
}

export enum TimeUnitType {
  millisecond = 'millisecond',
  second = 'second',
  minute = 'minute',
  hour = 'hour',
  day = 'day',
  week = 'week',
  month = 'month',
  year = 'year',
}

export enum AlarmStatus {
  active = 'active',
  inactive = 'inactive',
}

export enum AsyncTaskType {
  export_crusher_main_dashboard = 'export_crusher_main_dashboard',
}

export enum AsyncTaskStatus {
  pending = 'pending',
  processing = 'processing',
  completed = 'completed',
  failed = 'failed',
}

export enum CrusherDrawerType {
  alarms = 'alarms',
  export = 'export',
  info = 'info',
  maintenance = 'maintenance',
  settings = 'settings',
  timeline = 'timeline',
  trending = 'trending',
}

/* *************************************************************************************************
 ******************************************** TYPES ************************************************
 ************************************************************************************************ */

export type Thresholds = {
  highAlarm?: number | null;
  highWarning?: number | null;
  lowAlarm?: number | null;
  lowWarning?: number | null;
};

/* *************************************************************************************************
 ****************************************** INTERFACES *********************************************
 ************************************************************************************************ */

// ********** LOGIN ********** //

export interface LoginAuthResponse {
  token: string;
}

// ********** CUSTOMER ********** //

export interface Customer {
  createdAt: number;
  domains: string[];
  id: string;
  logoUrl: string | null;
  name: string;
  status: string;
}

export interface CustomerStats {
  alarms: number | null;
  equipmentUnits: number | null;
  equipmentUnitsOnline: number | null;
  sites: number | null;
  users: number | null;
}

// ********** EQUIPMENT ********** //

export interface EquipmentAlarmThresholds {
  highAlarm?: number | null;
  highWarning?: number | null;
  lowAlarm?: number | null;
  lowWarning?: number | null;
}

export interface EquipmentModelParameter {
  name: string;
  value: string | number;
}

export interface EquipmentModel {
  alarmThresholds: EquipmentAlarmThresholds[];
  commercialName: string;
  createdAt: number;
  createdBy: string;
  deletedAt?: number | null;
  deletedBy?: string | null;
  equipmentCategory: EquipmentCategory;
  id: string;
  parameters: EquipmentModelParameter[];
  superiorPartNumber: string;
  updatedAt?: number | null;
  updatedBy?: string | null;
}

// ********** EQUIPMENT UNIT ********** //

export interface EquipmentUnit {
  createdAt: number;
  createdBy: string;
  customerUnitName: string;
  deletedAt: number | null;
  deletedBy: string | null;
  equipmentCategory: EquipmentCategory;
  id: string;
  idArea: string | null;
  idModel: string;
  idSite: string | null;
  installStatus: EquipmentUnitInstallStatus;
  installedAt: number | null;
  installedBy: string | null;
  serialNumber: string;
  updatedAt: number;
  updatedBy: string;
}

export interface EquipmentUnitDataParamConfig {
  minValue: number;
  maxValue: number;
  thresholds: EquipmentAlarmThresholds;
}

// ********** SITE ********** //

export interface Site {
  createdAt: number;
  createdBy: string;
  deletedAt: number | null;
  deletedBy: string | null;
  id: string;
  identification: string;
  imageUrl: string | null;
  lat: number;
  lng: number;
  name: string;
  superiorCode: string;
  updatedAt: number;
  updatedBy: string;
}

// ********** EDGE DEVICE ********** //

export interface EdgeDeviceOperationalStatus {
  timestamp: number;
  edgeStatus: EdgeDeviceOperationalStatusStatus;
}

// ********** USER ********** //

export interface User {
  email: string;
  generatedAt: number;
  id: string;
  language: string;
  name: string;
  type: UserType;
}

export interface GPS {
  lat?: number | null;
  lng?: number | null;
  gpsTimestamp?: number | null;
}

// ********** LAYOUT ********** //

export interface BreakpointSize {
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
  xxl: number;
}

export enum BreakpointName {
  xs = 'xs',
  sm = 'sm',
  md = 'md',
  lg = 'lg',
  xl = 'xl',
  xxl = 'xxl',
}

export interface GridBreakpointConfig {
  cols?: number;
  offset?: number;
}

export interface GridBreakpointsGutter {
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
  xxl?: number;
}

/* *************************************************************************************************
 ****************************************** LAYOUT *************************************************
 ************************************************************************************************ */

/* Equipment Model Create */

export interface EquipmentModelCreateProps {
  testId?: string;
  category?: string;
  isDrawerOpen: boolean;
  onCloseDrawer: () => void;
  onChange: (field: string, value: string | string[]) => void;
  equipmentModel: Record<string, any>;
  onCreate: () => void;
}
